import * as React from "react";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import QRCodeGenerator from "qrcode";
import { getIsLoadingQR, getQR, getQRError } from "../../reducers/methods";
import { WIDGET_URL } from "../../config";
import "./QRCode.css";

export interface QRCodeComponentProps extends ComponentBaseProps {
  qrReferral: ReturnType<typeof getQR>;
  classNames: string;
  label: string;
  style: { [styleProp: string]: string };
  isLoading: ReturnType<typeof getIsLoadingQR>;
  error: ReturnType<typeof getQRError>;
  textStyle: { [styleProp: string]: string };
  loadingContent: string;
  errorStyle: { [styleProp: string]: string };
  errorContent: string;
  successContent: string;
  editStatus: "success" | "error" | "loading";
}

const EXAMPLE_LINK = "https://friendbuy.com";

export class QRCode extends ComponentBase<QRCodeComponentProps> {
  constructor(props: QRCodeComponentProps) {
    super(props);
  }
  generateQRSvg = () => {
    const link = this.props.isEditing
      ? EXAMPLE_LINK
      : this.props.qrReferral
        ? this.props.qrReferral.link
        : null;

    if (!link) {
      return this.setState({ qrSVG: undefined });
    }

    QRCodeGenerator.toString(
      link,
      {
        type: "svg",
      },
      (err, svg) => {
        if (err) {
          console.error(err);

          return this.setState({ qrSVG: undefined });
        }

        return this.setState({ qrSVG: svg });
      }
    );

    return null;
  };

  componentDidUpdate(prevProps: QRCodeComponentProps) {
    // Make sure the builder setup code in ComponentBase runs
    super.componentDidUpdate(prevProps);

    const link = this.props.qrReferral ? this.props.qrReferral.link : null;
    const prevLink = prevProps.qrReferral ? prevProps.qrReferral.link : null;
    if (this.props.isEditing && !this.state.qrSVG) {
      this.generateQRSvg();
    }
    if (link && link !== prevLink) {
      this.generateQRSvg();
    }
  }

  getEditContent = () => {
    const { editStatus, errorContent, successContent, loadingContent } =
      this.props;

    switch (editStatus) {
      case "success":
        return successContent;
      case "error":
        return errorContent;
      case "loading":
        return loadingContent;
      default:
        return successContent;
    }
  };

  getTextContent = () => {
    const { successContent, errorContent, loadingContent, error } = this.props;

    if (this.props.isEditing) {
      return this.getEditContent();
    }

    if (error) {
      return errorContent;
    } else if (this.state.qrSVG) {
      return successContent;
    } else {
      return loadingContent;
    }
  };

  getEditTextStyle = () => {
    const { textStyle, editStatus } = this.props;

    switch (editStatus) {
      case "error":
        return { ...textStyle, ...this.props.errorStyle };
      default:
        return textStyle;
    }
  };

  getTextStyle = () => {
    const { errorStyle = {}, textStyle, isEditing, error } = this.props;

    if (isEditing) {
      return this.getEditTextStyle();
    }

    if (error) {
      return { ...textStyle, ...errorStyle };
    } else {
      return textStyle;
    }
  };

  renderLoadingState = () => {
    return <img className="QRCode-image" src={`${WIDGET_URL}qrcodegif.gif`} />;
  };

  renderErrorState = () => {
    return (
      <img
        className="QRCode-image"
        src={`${WIDGET_URL}qrerrorstatenotext.png`}
      />
    );
  };

  renderSuccessState = () => {
    return (
      <div
        className="QRCode-content"
        dangerouslySetInnerHTML={{ __html: this.state.qrSVG || "" }}
      />
    );
  };

  getEditImageContent = () => {
    const { editStatus } = this.props;

    switch (editStatus) {
      case "loading":
        return this.renderLoadingState();
      case "error":
        return this.renderErrorState();
      default:
        return this.renderSuccessState();
    }
  };

  getImageContent = () => {
    const { error } = this.props;

    if (this.props.isEditing) {
      return this.getEditImageContent();
    }

    if (error) {
      return this.renderErrorState();
    } else if (this.state.qrSVG) {
      return this.renderSuccessState();
    } else {
      return this.renderLoadingState();
    }
  };

  render() {
    return (
      <div
        id={this.props.configName}
        style={{ ...this.props.style }}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        className={`QRCode-container`}
        aria-label={this.props.label}
        ref={(component) => (this.component = component)}
      >
        <div style={this.getTextStyle()}> {this.getTextContent()}</div>
        {this.getImageContent()}
      </div>
    );
  }
}
